import { useState, useEffect } from 'react';
import { Box, Typography, Paper, CircularProgress, Alert } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { WizardFormData } from '../schemas';
import { IronDomeSummaryItem } from '../IronDomeSummaryTable';
import DetailRow from './DetailRow';
import IronDomeSummaryTable from '../IronDomeSummaryTable';
import useApi from '../../../hooks/useApi';
import { parseIronDomeCSV } from '../utils/parseIronDomeCSV';

interface TrafficSourceSectionProps {
  getTrafficSourceName: (sourceId: string) => string;
  getFileName: (fileData: any) => string;
}

function TrafficSourceSection({
  getTrafficSourceName,
  getFileName,
}: TrafficSourceSectionProps) {
  const { watch } = useFormContext<WizardFormData>();
  const formData = watch();
  const { api: apiClient, createApiCall } = useApi();

  const [trafficSourceSummary, setTrafficSourceSummary] = useState<
    IronDomeSummaryItem[] | null
  >(null);
  const [trafficSourceLoading, setTrafficSourceLoading] = useState(false);
  const [trafficSourceError, setTrafficSourceError] = useState<string | null>(
    null
  );

  const fetchIronDomeSummary = createApiCall(
    async ({ signal }, csvData: any[]) => {
      const response = await apiClient.post(
        '/api/country/iron-dome/parseIronDome?summary=true',
        csvData,
        {
          signal,
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data.summary as IronDomeSummaryItem[];
    }
  );

  const fetchTrafficSourceSummary = async () => {
    if (!formData.trafficSourceIronDomeFile || !(formData.trafficSourceIronDomeFile instanceof File)) {
      setTrafficSourceSummary(null);
      return;
    }

    try {
      setTrafficSourceLoading(true);
      setTrafficSourceError(null);
      const parsedData = await parseIronDomeCSV(formData.trafficSourceIronDomeFile);

      const csvHasData =
        parsedData && parsedData.csvData && parsedData.csvData.length > 0;

      if (!csvHasData) return;

      const summary = await fetchIronDomeSummary().call(parsedData.csvData);
      setTrafficSourceSummary(summary);
    } catch (error: any) {
      console.error('Error fetching traffic source Iron Dome summary:', error);
      setTrafficSourceError('Failed to fetch summary');
      setTrafficSourceSummary(null);
    }
    setTrafficSourceLoading(false);
  };

  useEffect(() => {
    fetchTrafficSourceSummary();
  }, []);

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom color="primary">
        Traffic Source
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <DetailRow
          label="Auto Create"
          chipLabel={formData.autoCreateTrafficSource ? 'Yes' : 'No'}
          chipColor={formData.autoCreateTrafficSource ? 'success' : 'default'}
        />
        <DetailRow label="Network" value={formData.network} />
        {formData.network === 'xml' && formData.xmlRedirectField && (
          <DetailRow label="XML Redirect Field" value={formData.xmlRedirectField} />
        )}
        {formData.autoCreateTrafficSource ? (
          <>
            <DetailRow label="Name" value={formData.trafficSourceName} />
            <DetailRow
              label="X Parameters"
              chipLabel={
                formData.x1Parameters && formData.x1Parameters.length > 0
                  ? `${formData.x1Parameters.length} parameter(s)`
                  : 'None'
              }
              chipColor={
                formData.x1Parameters && formData.x1Parameters.length > 0
                  ? 'success'
                  : 'default'
              }
            />
            {formData.trafficSourceIronDomeFile && (
              <>
                <DetailRow
                  label="Iron Dome File"
                  value={getFileName(formData.trafficSourceIronDomeFile)}
                />
                {trafficSourceLoading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Loading summary...
                    </Typography>
                  </Box>
                )}
                {trafficSourceError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {trafficSourceError}
                  </Alert>
                )}
                {trafficSourceSummary && !trafficSourceLoading && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 'bold' }}
                    >
                      Iron Dome Summary:
                    </Typography>
                    <IronDomeSummaryTable summaryData={trafficSourceSummary} />
                  </Box>
                )}
              </>
            )}
          </>
        ) : (
          <DetailRow
            label="Selected Source"
            value={getTrafficSourceName(formData.selectedTrafficSourceId || '')}
          />
        )}
      </Box>
    </Paper>
  );
}

export default TrafficSourceSection;
